/**
 * Utility functions for formatting age display, especially for unidentified remains
 * which have estimated age ranges from NamUs data.
 */

/**
 * Format age for display, handling age ranges for unidentified remains
 * @param {Object} caseData - The case data object
 * @param {Object} options - Display options
 * @param {boolean} options.compact - Use compact format for mobile (default: false)
 * @param {boolean} options.showEstimated - Show "(Estimated)" label (default: true for unidentified)
 * @returns {string} Formatted age string
 */
export const formatAge = (caseData, options = {}) => {
  if (!caseData) return 'Age unknown';
  
  const { compact = false, showEstimated } = options;
  
  // Check if this is an unidentified case
  const isUnidentified = caseData.case_type === 'unidentified_person' || 
                         caseData.case_type === 'unidentified' ||
                         caseData.source === 'namus_unidentified' ||
                         caseData.source === 'doenetwork';
  
  // Get age range values - check multiple possible field names
  const ageMin = caseData.estimated_age_min ?? caseData.age_min ?? null;
  const ageMax = caseData.estimated_age_max ?? caseData.age_max ?? null;
  const singleAge = caseData.age ?? caseData.current_age ?? null;
  
  // Parse age_range string if available (e.g., "19-29")
  let parsedMin = ageMin;
  let parsedMax = ageMax;
  
  if (!parsedMin && !parsedMax && caseData.age_range) {
    // Ensure age_range is a string before calling .match()
    const ageRangeStr = String(caseData.age_range);
    const rangeMatch = ageRangeStr.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch) {
      parsedMin = parseInt(rangeMatch[1]);
      parsedMax = parseInt(rangeMatch[2]);
    } else {
      // Try parsing as single number
      const singleNum = parseInt(ageRangeStr);
      if (!isNaN(singleNum)) {
        parsedMin = singleNum;
        parsedMax = singleNum;
      }
    }
  }
  
  // Determine if we should show estimated label
  const shouldShowEstimated = showEstimated !== undefined ? showEstimated : isUnidentified;
  const estimatedSuffix = shouldShowEstimated ? ' (Estimated)' : '';
  const compactEstimatedSuffix = shouldShowEstimated ? ' (Est.)' : '';
  
  // Format based on available data
  if (parsedMin !== null && parsedMax !== null && parsedMin !== parsedMax) {
    // Both min and max exist and are different - show range
    if (compact) {
      return `${parsedMin}–${parsedMax}`;
    }
    return `${parsedMin}–${parsedMax} years${estimatedSuffix}`;
  }
  
  if (parsedMin !== null && parsedMax === null) {
    // Only minimum exists
    if (compact) {
      return `${parsedMin}+`;
    }
    return `${parsedMin}+ years${estimatedSuffix}`;
  }
  
  if (parsedMin === null && parsedMax !== null) {
    // Only maximum exists
    if (compact) {
      return `≤${parsedMax}`;
    }
    return `Up to ${parsedMax} years${estimatedSuffix}`;
  }
  
  if (parsedMin !== null && parsedMin === parsedMax) {
    // Min and max are the same
    if (compact) {
      return `${parsedMin}`;
    }
    return `${parsedMin} years old${isUnidentified ? compactEstimatedSuffix : ''}`;
  }
  
  // Fall back to single age value
  if (singleAge !== null) {
    if (compact) {
      return `${singleAge}`;
    }
    // For unidentified cases with only a single age, add estimated label
    if (isUnidentified) {
      return `${singleAge} years${compactEstimatedSuffix}`;
    }
    return `${singleAge} years old`;
  }
  
  return 'Age unknown';
};

/**
 * Get age range object for matching calculations
 * @param {Object} caseData - The case data object
 * @returns {Object} { min: number|null, max: number|null }
 */
export const getAgeRange = (caseData) => {
  if (!caseData) return { min: null, max: null };
  
  let ageMin = caseData.estimated_age_min ?? caseData.age_min ?? null;
  let ageMax = caseData.estimated_age_max ?? caseData.age_max ?? null;
  
  // Parse age_range string if available
  if (!ageMin && !ageMax && caseData.age_range) {
    // Ensure age_range is a string before calling .match()
    const ageRangeStr = String(caseData.age_range);
    const rangeMatch = ageRangeStr.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch) {
      ageMin = parseInt(rangeMatch[1]);
      ageMax = parseInt(rangeMatch[2]);
    } else {
      // Try parsing as single number
      const singleNum = parseInt(ageRangeStr);
      if (!isNaN(singleNum)) {
        ageMin = singleNum;
        ageMax = singleNum;
      }
    }
  }
  
  // Fall back to single age
  if (ageMin === null && ageMax === null) {
    const singleAge = caseData.age ?? caseData.current_age ?? null;
    if (singleAge !== null) {
      ageMin = singleAge;
      ageMax = singleAge;
    }
  }
  
  return { min: ageMin, max: ageMax };
};

/**
 * Check if two age ranges overlap (for matching)
 * @param {Object} range1 - First age range { min, max }
 * @param {Object} range2 - Second age range { min, max }
 * @param {number} tolerance - Years of tolerance for near-matches (default: 3)
 * @returns {Object} { overlaps: boolean, strength: 'strong'|'moderate'|'weak'|'none' }
 */
export const checkAgeOverlap = (range1, range2, tolerance = 3) => {
  if (!range1?.min && !range1?.max) return { overlaps: false, strength: 'none' };
  if (!range2?.min && !range2?.max) return { overlaps: false, strength: 'none' };
  
  const min1 = range1.min ?? range1.max;
  const max1 = range1.max ?? range1.min;
  const min2 = range2.min ?? range2.max;
  const max2 = range2.max ?? range2.min;
  
  // Check for direct overlap
  if (max1 >= min2 && max2 >= min1) {
    // Calculate overlap percentage
    const overlapStart = Math.max(min1, min2);
    const overlapEnd = Math.min(max1, max2);
    const overlapSize = overlapEnd - overlapStart + 1;
    const range1Size = max1 - min1 + 1;
    const range2Size = max2 - min2 + 1;
    const overlapPct = overlapSize / Math.min(range1Size, range2Size);
    
    if (overlapPct >= 0.5) return { overlaps: true, strength: 'strong' };
    return { overlaps: true, strength: 'moderate' };
  }
  
  // Check for near-overlap with tolerance
  const gap = Math.min(Math.abs(min1 - max2), Math.abs(min2 - max1));
  if (gap <= tolerance) {
    return { overlaps: true, strength: 'weak' };
  }
  
  return { overlaps: false, strength: 'none' };
};

export default { formatAge, getAgeRange, checkAgeOverlap };
